<template>
  <div class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 h-full flex flex-col relative z-10">
    <div class="trezo-card-content relative flex-1">
      <!-- Title with Icon -->
      <div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 dark:from-purple-500 dark:to-indigo-600
                    flex items-center justify-center shadow-lg">
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
        </div>
        <span class="block text-sm font-semibold text-slate-600 dark:text-slate-300 tracking-wide uppercase">
          {{ directT('dashboard.crm.leadConversion') }}
        </span>
      </div>

      <!-- Main Conversion Rate -->
      <div class="mb-2">
        <div v-if="loading" class="flex items-center gap-2">
          <div class="h-8 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse w-24"></div>
        </div>
        <div v-else class="flex items-center gap-2">
          <h2 class="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
            {{ conversionRate }}%
          </h2>
        </div>
      </div>

      <!-- Chart Container -->
      <div class="absolute -top-[28px] ltr:-right-[9px] rtl:-left-[9px] max-w-[120px]">
        <apexchart
          v-if="isClient && !loading"
          type="area"
          height="95"
          :options="barOptions"
          :series="series"
        ></apexchart>
        <div v-else-if="isClient && loading"
             class="h-[95px] flex items-center justify-center">
          <div class="w-20 h-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
        </div>
      </div>

      <!-- Bottom Stats Section -->
      <div class="mt-[25px] md:mt-[34px] flex items-center justify-between">
        <!-- Status Badge -->
        <span
          v-if="!hasPreviousPeriodData"
          class="inline-flex items-center gap-1 text-sm font-semibold py-2 px-3 rounded-xl
                 text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {{ directT('dashboard.noPreviousData') }}
        </span>
        <span
          v-else
          class="inline-flex items-center gap-1 text-sm font-semibold py-2 px-3 rounded-xl
                 transition-all duration-300 shadow-sm"
          :class="{
            'text-success-700 py-[1px] px-[8.3px] border border-success-300 bg-success-100 rounded-xl': percentageDifference > 0,
            'text-danger-700 py-[1px] px-[8.3px] border border-danger-300 bg-danger-100 rounded-xl': percentageDifference < 0,
            'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#172036] border border-gray-200 dark:border-[#172036]': percentageDifference === 0
          }"
        >
          <svg v-if="percentageDifference > 0" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11l5-5m0 0l5 5m-5-5v12"></path>
          </svg>
          <svg v-else-if="percentageDifference < 0" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 13l-5 5m0 0l-5-5m5 5V6"></path>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14"></path>
          </svg>
          {{ percentageDifference > 0 ? '+' : '' }}{{ formatPercentage(percentageDifference) }}%
        </span>

        <!-- Time Period -->
        <span class="block text-sm font-medium text-slate-500 dark:text-slate-400 tracking-wide">
          {{ directT('dashboard.last30Days') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onUnmounted } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { useI18n } from "vue-i18n";
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Type definition for nested translation object structure
type TranslationObject = Record<string, any>;

// Store direct references to translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Define the type for lead objects
interface LeadItem {
  id: number;
  status: string | null;
}

export default defineComponent({
  name: "LeadConversionChart",
  setup() {
    const isClient = ref(false);
    const loading = ref(true);
    const conversionRate = ref(0);
    const previousConversionRate = ref(0);
    const percentageDifference = ref(0);
    const hasPreviousPeriodData = ref(false);
    const organizationId = ref<string | null>(null);
    const weeklyData = ref<number[]>([0, 0, 0, 0]);
    const refreshTimer = ref<number | null>(null);

    // i18n setup
    const { locale } = useI18n();

    // Format percentage - show decimal only if needed
    const formatPercentage = (value: number): string => {
      const rounded = Math.round(value * 10) / 10;
      return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];

      for (const part of parts) {
        if (!result || !(part in result)) {
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }

      if (typeof result !== 'string') {
        return key;
      }

      // Handle parameter substitution if needed
      if (Object.keys(params).length > 0) {
        let finalResult: string = result;
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          finalResult = finalResult.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
        });
        return finalResult;
      }

      return result;
    };

    const series = computed(() => [
      {
        name: directT('dashboard.crm.leadConversion'),
        data: weeklyData.value,
      },
    ]);

    const barOptions = ref({
      chart: {
        type: "area",
        height: 95,
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350
          }
        }
      },
      colors: ["#605DFF"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
        borderColor: "#ECEEF2",
      },
      stroke: {
        curve: "smooth",
        width: 1,
      },
      xaxis: {
        categories: [
          directT('weeks.week1'),
          directT('weeks.week2'),
          directT('weeks.week3'),
          directT('weeks.week4')
        ],
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        labels: {
          show: false,
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        show: false,
        labels: {
          formatter: (val: string) => {
            return val + "%";
          },
          style: {
            colors: "#64748B",
            fontSize: "12px",
          },
        },
        axisBorder: {
          show: false,
          color: "#ECEEF2",
        },
        axisTicks: {
          show: false,
          color: "#ECEEF2",
        },
      },
      legend: {
        show: false,
        position: "top",
        fontSize: "12px",
        horizontalAlign: "left",
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
        labels: {
          colors: "#64748B",
        },
        markers: {
          width: 9,
          height: 9,
          offsetX: -2,
          offsetY: -0.5,
          radius: 2,
        },
      },
    });

    // Get date from X days ago in ISO format
    const getDateXDaysAgo = (days: number) => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString();
    };

    // Get user's organization ID
    const fetchUserOrganization = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.warn('User not authenticated');
          return null;
        }
        
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();
        
        if (profileError || !userProfile?.org_id) {
          console.warn('User has no organization');
          return null;
        }
        
        return userProfile.org_id as string;
      } catch (error) {
        console.error('Error fetching user organization:', error);
        return null;
      }
    };

    // Calculate conversion rate
    const calculateConversionRate = async () => {
      try {
        loading.value = true;

        // Get organization ID first
        const orgId = await fetchUserOrganization();
        if (!orgId) {
          loading.value = false;
          return;
        }
        organizationId.value = orgId;

        const thirtyDaysAgo = getDateXDaysAgo(30);
        const sixtyDaysAgo = getDateXDaysAgo(60);

        // Build query for current period using leads with org_id filter
        const { data: currentLeads, error: currentError } = await supabase
          .from('leads')
          .select('id, status')
          .eq('org_id', orgId)
          .gte('created_at', thirtyDaysAgo);

        if (currentError) {
          loading.value = false;
          return;
        }

        // Count leads with "Close" status
        const currentClosedLeads = currentLeads ? (currentLeads as LeadItem[]).filter(lead =>
          lead.status && lead.status === "Close"
        ).length : 0;

        // Calculate conversion rate for current period
        const currentTotal = currentLeads ? currentLeads.length : 0;

        // More precise calculation to avoid issues
        const currentRate = currentTotal > 0
          ? Math.round((currentClosedLeads / currentTotal) * 100)
          : 0;

        conversionRate.value = currentRate;

        // Build query for previous period using leads with org_id filter
        const { data: previousLeads, error: previousError } = await supabase
          .from('leads')
          .select('id, status')
          .eq('org_id', orgId)
          .gte('created_at', sixtyDaysAgo)
          .lt('created_at', thirtyDaysAgo);

        if (previousError) {
          loading.value = false;
          return;
        }

        // Count leads with "Close" status
        const previousClosedLeads = previousLeads ? (previousLeads as LeadItem[]).filter(lead =>
          lead.status && lead.status === "Close"
        ).length : 0;

        // Calculate conversion rate for previous period
        const previousTotal = previousLeads ? previousLeads.length : 0;
        const previousRate = previousTotal > 0 ? Math.round((previousClosedLeads / previousTotal) * 100) : 0;

        previousConversionRate.value = previousRate;

        // Determine if we have previous period data
        hasPreviousPeriodData.value = previousTotal > 0;

        // Calculate the percentage difference
        if (hasPreviousPeriodData.value) {
          if (previousRate === 0) {
            percentageDifference.value = currentRate > 0 ? 100 : 0;
          } else {
            percentageDifference.value = Math.round(currentRate - previousRate);
          }
        } else {
          percentageDifference.value = 0;
        }

        // Set weekly data for chart
        await fetchWeeklyData();

      } catch (error) {
        console.error('Error in calculateConversionRate:', error);
      } finally {
        loading.value = false;
      }
    };

    // Fetch weekly data for chart
    const fetchWeeklyData = async () => {
      try {
        if (!organizationId.value) {
          weeklyData.value = [0, 0, 0, 0];
          return;
        }

        const weeks = [
          { start: getDateXDaysAgo(28), end: getDateXDaysAgo(21) },
          { start: getDateXDaysAgo(21), end: getDateXDaysAgo(14) },
          { start: getDateXDaysAgo(14), end: getDateXDaysAgo(7) },
          { start: getDateXDaysAgo(7), end: getDateXDaysAgo(0) }
        ];

        const weeklyRates = [];

        for (let i = 0; i < weeks.length; i++) {
          const week = weeks[i];
          // Build query for weekly data with org_id filter
          const { data: weekLeads, error: weekError } = await supabase
            .from('leads')
            .select('id, status')
            .eq('org_id', organizationId.value)
            .gte('created_at', week.start)
            .lt('created_at', week.end);

          if (weekError) {
            continue;
          }

          // Calculate weekly conversion rate with Close status
          const weekClosedLeads = weekLeads ? (weekLeads as LeadItem[]).filter(lead =>
            lead.status && lead.status === "Close"
          ).length : 0;

          const weekTotal = weekLeads ? weekLeads.length : 0;
          const weekRate = weekTotal > 0 ? Math.round((weekClosedLeads / weekTotal) * 100) : 0;

          weeklyRates.push(weekRate);
        }

        weeklyData.value = weeklyRates;

      } catch (error) {
        console.error('Error in fetchWeeklyData:', error);
        weeklyData.value = [0, 0, 0, 0];
      }
    };

    onMounted(() => {
      isClient.value = true;
      calculateConversionRate();
    });

    onUnmounted(() => {
      // Clear the refresh timer when component is unmounted
      if (refreshTimer.value) {
        clearInterval(refreshTimer.value);
      }
    });

    return {
      isClient,
      loading,
      conversionRate,
      previousConversionRate,
      percentageDifference,
      hasPreviousPeriodData,
      series,
      barOptions,
      directT,
      formatPercentage,
    };
  },
});
</script>
